"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.monthCells = monthCells;
exports.DatePicker = DatePicker;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_dom_1 = require("react-dom");
const shared_1 = require("@forecastify/shared");
const cn_1 = require("../lib/cn");
const overlay_1 = require("../lib/overlay");
const button_1 = require("./button");
const icon_1 = require("./icon");
/** The `1..n` days of a month, with the Monday-based lead offset before the first. */
function monthCells(year, month) {
    const first = (0, shared_1.toWireDate)(new Date(year, month, 1));
    /* Monday-based, from the shared helper — `getDay()` is Sunday-based and converting it here
     * would be the fifth copy of `(getDay() + 6) % 7` in this codebase. */
    const lead = (0, shared_1.wireDayOfWeek)(first) ?? 0;
    const length = new Date(year, month + 1, 0).getDate();
    const days = [];
    for (let day = 1; day <= length; day++)
        days.push((0, shared_1.toWireDate)(new Date(year, month, day)));
    return { lead, days };
}
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ One date, on a Monday-first month grid.                                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **Wire dates in, wire dates out — never a `Date` across the boundary.** Every value this
 * component emits goes through `toWireDate`, so the UTC off-by-one that `hard-won.md` P1-T7-6
 * records cannot happen here. A `Date` prop would push the conversion onto every caller, which
 * is how that defect reached four separate sites in the outgoing project.
 *
 * ⚠️ **The trigger shows the wire form**, `2026-08-24`, in mono. That is what the design
 * system's implementation renders, and it disagrees with its own `.prompt.md` (which claims
 * `aug 24.`) and with its readme (`2026. augusztus 19.` in headers). Three formats, three
 * sources; the implementation is what the prototypes actually draw. A caller wanting the short
 * Hungarian form formats it itself — this component does not own display formatting, because a
 * formatter here would be a fourth opinion.
 */
function DatePicker({ value, onChange, today, open: openProp, onOpenChange, labels, clearable = true, ariaLabel, className, buttonClassName, formatValue, unit = 'day', }) {
    const [selfOpen, setSelfOpen] = (0, react_1.useState)(false);
    /* ⚠️ Uncontrolled unless BOTH ends are wired — a `open` with no `onOpenChange` would be a
       calendar that cannot be dismissed, which is worse than one that cannot be opened. */
    const controlled = openProp !== undefined && onOpenChange !== undefined;
    const open = controlled ? openProp : selfOpen;
    const setOpen = (next) => {
        if (controlled)
            onOpenChange(next);
        else
            setSelfOpen(next);
    };
    const selected = value === null || value === undefined ? null : (0, shared_1.fromWireDate)(value);
    const base = selected ?? (0, shared_1.fromWireDate)(today) ?? new Date();
    const [cursor, setCursor] = (0, react_1.useState)({ year: base.getFullYear(), month: base.getMonth() });
    const { surface, trigger } = (0, overlay_1.useDismiss)(open, () => {
        setOpen(false);
    });
    const { rect, measure } = (0, overlay_1.useAnchorRect)(open, trigger);
    /* Reopening re-centres on the selection, so a picker opened months away from the chosen date
     * does not require paging back. Keyed on `open` alone: re-running on `value` would yank the
     * grid out from under someone paging through months. */
    (0, react_1.useEffect)(() => {
        if (!open)
            return;
        const at = selected ?? (0, shared_1.fromWireDate)(today) ?? new Date();
        setCursor({ year: at.getFullYear(), month: at.getMonth() });
        /* ⚠️ And on the unit's own view, always. Reopening into the year grid because that is where
           the last visit ended is a picker that has forgotten what it is for. */
        setView(unit === 'month' ? 'months' : 'days');
    }, [open]);
    const { lead, days } = monthCells(cursor.year, cursor.month);
    const pick = (wire) => {
        setOpen(false);
        /* ⚠️ The unit's own first day: a week is its Monday, a month its first. */
        onChange?.(unit === 'week'
            ? ((0, shared_1.wireMonday)(wire) ?? wire)
            : unit === 'month'
                ? `${wire.slice(0, 7)}-01`
                : wire);
    };
    const chosenWeek = unit === 'week' && value != null ? (0, shared_1.wireMonday)(value) : null;
    /**
     * ⚠⚠ **THREE ZOOM LEVELS, BECAUSE ONE ARROW PER MONTH IS TWENTY-FOUR CLICKS TO NEXT YEAR.**
     * `qol-fixes.md` item 24. Days zoom out to months, months to a twelve-year grid, and picking at
     * any level zooms back in one step rather than closing — so a date two years out is three clicks.
     */
    const [view, setView] = (0, react_1.useState)('days');
    /* ⚠️ The grid starts on a multiple of 12, so paging never lands the same year in two blocks. */
    const yearBlock = cursor.year - (((cursor.year % 12) + 12) % 12);
    const step = (delta) => {
        setCursor((at) => {
            if (view === 'days') {
                const moved = new Date(at.year, at.month + delta, 1);
                return { year: moved.getFullYear(), month: moved.getMonth() };
            }
            /* ⚠️ A year at a time in the month view, twelve in the year view — the unit the reader
               is looking at, which is the whole point of zooming out. */
            return { ...at, year: at.year + delta * (view === 'months' ? 1 : 12) };
        });
    };
    const popover = open && rect !== null
        ? (0, react_dom_1.createPortal)((0, jsx_runtime_1.jsxs)("div", { ref: surface, role: "dialog", "aria-label": labels.dialogLabel, style: (0, overlay_1.anchorTo)(rect, { width: 264 }), className: "pop-in bg-popover text-popover-foreground shadow-pop rounded-md border p-2", children: [(0, jsx_runtime_1.jsxs)("div", { className: "flex items-center gap-1 px-1 pb-1.5", children: [(0, jsx_runtime_1.jsx)(button_1.Button, { variant: "ghost", size: "icon-sm", "aria-label": view === 'days'
                                ? labels.previousMonth
                                : (labels.previousPeriod ?? labels.previousMonth), onClick: () => {
                                step(-1);
                            }, children: (0, jsx_runtime_1.jsx)(icon_1.Icon, { name: "chevron-left", size: 14 }) }), (0, jsx_runtime_1.jsxs)("button", { type: "button", "aria-label": labels.zoomOut, disabled: view === 'years', onClick: () => {
                                setView((at) => (at === 'days' ? 'months' : 'years'));
                            }, className: "hover:bg-accent flex-1 rounded-sm py-0.5 text-center text-[12.5px] font-medium disabled:cursor-default disabled:hover:bg-transparent", children: [view === 'days'
                                    ? `${String(cursor.year)}. ${labels.months[cursor.month] ?? ''}`
                                    : null, view === 'months' ? cursor.year : null, view === 'years' ? `${String(yearBlock)} – ${String(yearBlock + 11)}` : null] }), (0, jsx_runtime_1.jsx)(button_1.Button, { variant: "ghost", size: "icon-sm", "aria-label": view === 'days' ? labels.nextMonth : (labels.nextPeriod ?? labels.nextMonth), onClick: () => {
                                step(1);
                            }, children: (0, jsx_runtime_1.jsx)(icon_1.Icon, { name: "chevron-right", size: 14 }) })] }), view === 'months' ? ((0, jsx_runtime_1.jsx)("div", { className: "grid grid-cols-3 gap-1", children: labels.months.map((name, index) => ((0, jsx_runtime_1.jsx)("button", { type: "button", "aria-pressed": index === cursor.month, onClick: () => {
                            /* ⚠️ For a month picker this level IS the answer. */
                            if (unit === 'month') {
                                pick((0, shared_1.toWireDate)(new Date(cursor.year, index, 1)));
                                return;
                            }
                            setCursor((at) => ({ ...at, month: index }));
                            setView('days');
                        }, className: (0, cn_1.cn)('hover:bg-accent grid h-8 place-items-center rounded-sm text-[12px] capitalize', index === cursor.month &&
                            (unit !== 'month' ||
                                (selected !== null && selected.getFullYear() === cursor.year))
                            ? 'bg-primary text-primary-foreground'
                            : ''), children: name }, name))) })) : null, view === 'years' ? ((0, jsx_runtime_1.jsx)("div", { className: "grid grid-cols-3 gap-1", children: Array.from({ length: 12 }, (_, index) => yearBlock + index).map((year) => ((0, jsx_runtime_1.jsx)("button", { type: "button", "aria-pressed": year === cursor.year, onClick: () => {
                            setCursor((at) => ({ ...at, year }));
                            setView('months');
                        }, className: (0, cn_1.cn)('hover:bg-accent grid h-8 place-items-center rounded-sm text-[12px] tabular-nums', year === cursor.year ? 'bg-primary text-primary-foreground' : ''), children: year }, year))) })) : null, (0, jsx_runtime_1.jsxs)("div", { className: (0, cn_1.cn)('grid grid-cols-7 gap-0.5', view === 'days' ? '' : 'hidden'), children: [labels.weekdays.map((day) => ((0, jsx_runtime_1.jsx)("div", { className: "text-muted-foreground grid h-7 place-items-center text-[10.5px] font-medium", children: day }, day))), Array.from({ length: lead }, (_, index) => ((0, jsx_runtime_1.jsx)("span", {}, `lead-${String(index)}`))), days.map((wire) => {
                            const isSelected = unit === 'week'
                                ? chosenWeek !== null && (0, shared_1.wireMonday)(wire) === chosenWeek
                                : value === wire;
                            const isToday = wire === today;
                            return ((0, jsx_runtime_1.jsx)("button", { type: "button", onClick: () => {
                                    pick(wire);
                                }, "aria-current": isToday ? 'date' : undefined, "aria-pressed": isSelected, className: (0, cn_1.cn)('grid h-7 place-items-center rounded-md text-[12px] tabular-nums transition-colors', isSelected
                                    ? 'bg-primary text-primary-foreground font-medium'
                                    : isToday
                                        ? 'bg-accent font-medium'
                                        : 'hover:bg-accent'), children: Number(wire.slice(8)) }, wire));
                        })] }), (0, jsx_runtime_1.jsxs)("div", { className: "mt-1.5 flex items-center gap-1 border-t pt-1.5", children: [(0, jsx_runtime_1.jsx)(button_1.Button, { variant: "ghost", size: "sm", className: "flex-1", onClick: () => {
                                pick(today);
                            }, children: labels.today }), clearable ? ((0, jsx_runtime_1.jsx)(button_1.Button, { variant: "ghost", size: "sm", className: "text-muted-foreground", onClick: () => {
                                setOpen(false);
                                /* ⚠️ `null`, not `''`. An absent date is absent; the empty string is a
                                 * value that then flows into a wire payload as `""` and fails validation
                                 * one layer down, where the message names a field nobody edited. */
                                onChange?.(null);
                            }, children: labels.clear })) : null] })] }), document.body)
        : null;
    return ((0, jsx_runtime_1.jsxs)("div", { ref: trigger, className: (0, cn_1.cn)('relative', className), children: [(0, jsx_runtime_1.jsxs)("button", { type: "button", onClick: () => {
                    if (open) {
                        setOpen(false);
                    }
                    else {
                        measure();
                        setOpen(true);
                    }
                }, "aria-haspopup": "dialog", "aria-expanded": open, ...(ariaLabel === undefined ? {} : { 'aria-label': ariaLabel }), className: (0, cn_1.cn)('border-input hover:bg-accent/60 focus-visible:ring-ring/50 shadow-xs flex h-8 w-full items-center gap-2 rounded-md border bg-transparent px-2.5 text-left text-[12.5px] transition-colors outline-none focus-visible:ring-[3px]', buttonClassName), children: [(0, jsx_runtime_1.jsx)(icon_1.Icon, { name: "calendar", size: 14, className: "text-muted-foreground" }), (0, jsx_runtime_1.jsx)("span", { className: (0, cn_1.cn)('min-w-0 flex-1 truncate tabular-nums', selected === null && 'text-muted-foreground font-sans'), children: (0, jsx_runtime_1.jsxs)("span", { className: "inline-grid max-w-full", children: [(0, jsx_runtime_1.jsx)("span", { className: "truncate [grid-area:1/1]", children: selected === null || value == null
                                        ? labels.placeholder
                                        : formatValue === undefined
                                            ? value
                                            : formatValue(value) }), widestValues(formatValue, value).map((text) => ((0, jsx_runtime_1.jsx)("span", { "aria-hidden": true, className: "invisible truncate [grid-area:1/1]", children: text }, text)))] }) })] }), popover] }));
}
/**
 * ⚠️ 21 Oct 2026 · **ONE WIDTH, WHATEVER PERIOD IS SHOWING.** Stepping from *Sep 7 – Dec 27* to
 * *Sep 28, 2026 – Jan 17, 2027* widened the button and moved every control beside it, and a
 * single-digit day narrowed it again. The label shares its grid cell with invisible copies of the
 * widest labels this formatter can produce — every month, a two-digit day, across a year — so the
 * cell is always the widest of them, in whichever language the months are named.
 */
function widestValues(formatValue, value) {
    if (formatValue === undefined)
        return [];
    const year = Number((value ?? '').slice(0, 4)) || new Date().getFullYear();
    const texts = new Set();
    for (let month = 1; month <= 12; month += 1) {
        const mm = String(month).padStart(2, '0');
        /* The 1st and the 28th: a two-digit day, and a week that runs into the next month. */
        texts.add(formatValue(`${String(year)}-${mm}-01`));
        texts.add(formatValue(`${String(year)}-${mm}-28`));
    }
    return [...texts];
}
//# sourceMappingURL=date-picker.js.map